import { AssetType } from "../types/AssetType";

export class BlackScholesModelCalculationService {
    static readonly riskFreeRate: number = 0.04;
    static readonly dividendYield: number = 0;
    private static readonly calendarDaysInYear: number = 365;

    getPremium(optionType: AssetType, underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        const d1 = this.getD1(s, k, t, sigma, r, q);
        const d2 = this.getD2(t, sigma, d1);

        switch (optionType) {
            case AssetType.Call:
                return s * Math.exp(-q * t) * this.getCdf(d1) - k * Math.exp(-r * t) * this.getCdf(d2);

            case AssetType.Put:
                return k * Math.exp(-r * t) * this.getCdf(-d2) - s * Math.exp(-q * t) * this.getCdf(-d1);

            default:
                throw new Error("BlackScholesModelCalculationService - AssetType is not an option.");
        }
    }

    getDelta(optionType: AssetType, underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        const d1 = this.getD1(s, k, t, sigma, r, q);
        const discountFactor = Math.exp(-q * t);

        switch (optionType) {
            case AssetType.Call:
                return discountFactor * this.getCdf(d1);

            case AssetType.Put:
                return discountFactor * (this.getCdf(d1) - 1);

            default:
                throw new Error("BlackScholesModelCalculationService - AssetType is not an option.");
        }
    }

    getGamma(underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        const d1 = this.getD1(s, k, t, sigma, r, q);
        const phi = this.getStdpdf(d1);

        return (Math.exp(-q * t) * phi) / (s * sigma * Math.sqrt(t));
    }

    getVega(underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        const d1 = this.getD1(s, k, t, sigma, r, q);
        const phi = this.getStdpdf(d1);

        // Vega per 1% change in volatility
        return (s * Math.exp(-q * t) * phi * Math.sqrt(t)) / 100;
    }

    getCdf(x: number): number {
        // Abramowitz and Stegun approximation
        const a1 = 0.319381530;
        const a2 = -0.356563782;
        const a3 = 1.781477937;
        const a4 = -1.821255978;
        const a5 = 1.330274429;
        const p = 0.2316419;
        const t = 1 / (1 + p * Math.abs(x));

        const pdf = this.getStdpdf(x);
        const term = (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t;
        const cdf = Math.min(Math.max(1 - pdf * term, 0), 1);

        if (x === 0) {
            return 0.5;
        }

        return x > 0 ? cdf : 1 - cdf;
    }

    getTheta(optionType: AssetType, underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        const d1 = this.getD1(s, k, t, sigma, r, q);
        const d2 = this.getD2(t, sigma, d1);
        const pdfD1 = this.getStdpdf(d1);
        const nd1 = this.getCdf(d1);
        const nd2 = this.getCdf(d2);
        const nMinusD1 = this.getCdf(-d1);
        const nMinusD2 = this.getCdf(-d2);

        const term1 = -(s * Math.exp(-q * t) * pdfD1 * sigma) / (2 * Math.sqrt(t));
        const dailyFactor = 1 / BlackScholesModelCalculationService.calendarDaysInYear;

        if (optionType === AssetType.Call) {
            const dividendYieldTerm = q * s * Math.exp(-q * t) * nd1;
            const interestRateTerm = r * k * Math.exp(-r * t) * nd2;
            return (term1 + dividendYieldTerm - interestRateTerm) * dailyFactor;
        } else if (optionType === AssetType.Put) {
            const dividendYieldTerm = q * s * Math.exp(-q * t) * nMinusD1;
            const interestRateTerm = r * k * Math.exp(-r * t) * nMinusD2;
            return (term1 - dividendYieldTerm + interestRateTerm) * dailyFactor;
        } else {
            throw new Error("Invalid option type.");
        }
    }

    getRho(optionType: AssetType, underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number = BlackScholesModelCalculationService.riskFreeRate,
        dividendYield: number = BlackScholesModelCalculationService.dividendYield
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        if (optionType !== AssetType.Call && optionType !== AssetType.Put) {
            throw new Error("BlackScholesModelCalculationService - AssetType is not an option.");
        }

        const d1: number = this.getD1(s, k, t, sigma, r, q);
        const d2: number = this.getD2(t, sigma, d1);

        switch (optionType) {
            case AssetType.Call: {
                return (t * k * Math.exp(-r * t) * this.getCdf(d2)) / 100;
            }

            case AssetType.Put: {
                return (-t * k * Math.exp(-r * t) * this.getCdf(-d2)) / 100;
            }
        }
    }

    getTimeToExpirationInShareOfYear(expireAt: Date, from?: Date): number {
        const fromDate = from === undefined || from === null
            ? new Date()
            : from;
        const daysToExpiration = ((new Date(expireAt)).getTime() - new Date(fromDate).getTime()) / (1000 * 60 * 60 * 24);

        return daysToExpiration !== 0
            ? daysToExpiration / 365
            : Math.pow(10, -10);
    }

    getD1(underlyingPrice: number, strikePrice: number,
        timeToExpirationInShareOfYear: number, volatility: number,
        riskFreeInterestRate: number, dividendYield: number
    ): number {
        const s = underlyingPrice;
        const k = strikePrice;
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const r = riskFreeInterestRate;
        const q = dividendYield;

        return (Math.log(s / k) + (r - q + 0.5 * sigma * sigma) * t) / (sigma * Math.sqrt(t));
    }

    getD2(timeToExpirationInShareOfYear: number, volatility: number, d1Value: number): number {
        const t = timeToExpirationInShareOfYear;
        const sigma = volatility;
        const d1 = d1Value;

        return d1 - sigma * Math.sqrt(t);
    }

    getStdpdf(x: number): number {
        return (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x);
    }
}

export const blackScholesModelCalculationService = new BlackScholesModelCalculationService();
